import React from 'react'
import { includes, map, get, filter, isEmpty, every, groupBy, each, sumBy } from 'lodash'
import { humanizeGB, humanizeGhz, humanize } from 'Decorators/SizeDecorator'
import TableCellList from "UI/Table/components/TableCellList";

const columns = [
  { Header: 'VDC', accessor: 'org_vdc' },
  { Header: 'Customer', accessor: 'customer_name', type: 'clientNameWithStatus' },
  { Header: 'Location', accessor: 'location' },
  { Header: 'vCPUs', accessor: 'vcpus' },
  { Header: 'Allocation Model', accessor: 'allocation_model' },
  { Header: 'VDC CPU Speed', accessor: 'vcpu_speed_mhz', Cell: ({ row }) => (humanize(row.vcpu_speed_mhz, 'MHz')) },
  {
    Header: 'CPU', width: 250,
    accessor: 'current_used_cpu_ghz',
    Cell: ({ original: data }) => {
      const list = [
        { label: 'Current used', value: `${humanize(data.current_used_cpu_ghz)} / ${humanize(data.configured_cpu_limit_ghz, 'GHz')}` },
        { label: 'Peak used', value: humanize(data.peak_used_cpu_ghz, 'GHz') },
        { label: 'Average used', value: humanize(data.average_used_cpu_ghz, 'GHz') }
      ]

      return <TableCellList list ={list} />
    }
  },
  {
    Header: 'RAM GB',
    accessor: 'current_used_ram_gb',
    width: 250,
    Cell: ({ original: data }) => {
      const list = [
        { label: 'Current used', value: `${humanize(data.current_used_ram_gb)} / ${humanize(data.configured_ram_limit_gb, 'GB')}` },
        { label: 'Peak used', value: humanize(data.peak_used_ram_gb, 'GB') },
        { label: 'Average used', value: humanize(data.average_used_ram_gb, 'GB') }
      ]

      return <TableCellList list ={list} />
    }
  },
  { Header: 'Reserved % CPU', accessor: 'cpu_guaranteed_percent', type: 'percent' },
  { Header: 'Reserved % Memory', accessor: 'memory_guaranteed_percent', type: 'percent' },
  { Header: 'Disk vSphere Provisioned', accessor: 'disk_vsphere_provisioned_gb', Cell: ({ row }) => (humanizeGB(row.disk_vsphere_provisioned_gb)) },
]

const filteredVDCConsumption = (filters, assets) => filter(assets, (asset) => {
  const filterResults = {}

  if (filters.customer != 'All') {
    filterResults['customer'] = includes(map(filters.customer, 'value'), get(asset, 'customer_name'))
  }

  if (filters.customerStatus != 'All') {
    filterResults['customerStatus'] = includes(map(filters.customerStatus, 'value'), get(asset, 'customer_status'))
  }

  if (filters.location != 'All') {
    filterResults['location'] = includes(map(filters.location, 'value'), get(asset, 'location'))
  }

  return (every(filterResults, (value, _key) => value === true) || isEmpty(filterResults))
})

const groupedVDCConsumption = (groupKey, assets) => {
  if (groupKey == 'global') {
    return assets
  }

  return (
    map(groupBy(assets, groupKey), (value, key) => (
      {
        [groupKey]: key,
        vdcs: value,
        vdc_count: value.length,
        current_used_cpu_ghz: sumBy(value, vdc => parseFloat(vdc.current_used_cpu_ghz)),
        current_used_ram_gb: sumBy(value, vdc => parseFloat(vdc.current_used_ram_gb)),
        configured_cpu_limit_ghz: sumBy(value, vdc => parseFloat(vdc.configured_cpu_limit_ghz)),
        configured_ram_limit_gb: sumBy(value, vdc => parseFloat(vdc.configured_ram_limit_gb)),
        vcpus: sumBy(value, vdc => parseFloat(vdc.vcpus)),
        storage: sumBy(value, vdc => parseFloat(vdc.disk_vsphere_provisioned_gb))
      }
    ))
  )
}

const groupByColumns = (groupBy, columns) => {
  if (groupBy == 'global') {
    return columns
  }

  const groupedColumns = [
    { Header: 'Current Used CPU GHz', accessor: 'current_used_cpu_ghz', Cell: ({ row }) => (humanizeGhz(row.current_used_cpu_ghz)) },
    { Header: 'Configured CPU Limit GHz', accessor: 'configured_cpu_limit_ghz', Cell: ({ row }) => (humanizeGhz(row.configured_cpu_limit_ghz)) },
    { Header: 'Current Used RAM GB', accessor: 'current_used_ram_gb', Cell: ({ row }) => (humanizeGB(row.current_used_ram_gb)) },
    { Header: 'Configured RAM Limit GB', accessor: 'configured_cpu_limit_ghz', Cell: ({ row }) => (humanizeGB(row.configured_cpu_limit_ghz)) },
    { Header: 'vCPUs', accessor: 'vcpus' },
    { Header: 'Storage', accessor: 'storage', Cell: ({ row }) => (humanizeGB(row.storage)) }
  ]

  if (groupBy == 'customer_name') {
    let groupedByClientColumns = groupedColumns.slice(0, groupedColumns.length)
    groupedByClientColumns.unshift({ Header: 'Customer', accessor: 'customer_name' })
    return groupedByClientColumns
  }

  if (groupBy == 'location') {
    let groupedByLocationColumns = groupedColumns.slice(0, groupedColumns.length)
    groupedByLocationColumns.unshift({ Header: 'Location', accessor: 'location' })
    return groupedByLocationColumns
  }
}


each(columns, column => column['filterable'] = true)
export { filteredVDCConsumption, groupByColumns, groupedVDCConsumption, columns }
